import React, { useEffect } from 'react';
import classNames from 'classnames/bind';

import HorizontalScrollable from '../../molecules/HorizontalScrollable';
import FallbackableImage from '../../molecules/FallbackableImage';
import Text from '../../atoms/Text';
import styles from './index.pcss';
import {
  getStoreName,
  gtmPromotionCmsClick,
  gtmPromotionCmsImpressions
} from '../../../modules/ga/GTMdatalayer';
import {
  checkCategoryImpressionPromoId,
  emptyBundleCategoryImpressionArray,
  getBundleCategoryImpressionArray,
  setBundleCategoryImpressionArray,
  setCategoryImpressionPromoId
} from '../../../utils/NavigationRevamp';

type CuratedItemImageData = {
  product: string;
  mobileProductListingImage: string;
  productGrid3ListingImage: string;
};

type CuratedItem = {
  filters: string;
  header: string;
  subheader: string;
  images: CuratedItemImageData;
};

type CuratedWidgetNestedData = {
  widgetName: string;
  widgetLevel: string;
  curatedWidgetItemList: CuratedItem;
};

interface CuratedWidgetProps {
  curatedWidgetData: CuratedWidgetNestedData;
  luxe: boolean;
  rowNumber: Number;
  bannerType: string;
  banners: any;
  heading: string | undefined;
  isAjiogram?: boolean;
}

interface CuratedProductProps {
  curatedItem: CuratedItem;
  idx: number;
  luxe: boolean;
  rowNumber: Number;
  widgetName: string;
  feedLogic: string;
  widgetLevelFromBanners: string;
  showDefault: boolean,
  heading: string;
  isAjiogram?: boolean;
}

const cx = classNames.bind(styles);

const RenderCuratedProductCard = (curatedProductProps: CuratedProductProps) => {
  const { curatedItem, luxe, idx, rowNumber, widgetName, feedLogic, widgetLevelFromBanners, showDefault = false, heading, isAjiogram = false } = curatedProductProps;
  const { header, subheader, images, filters } = curatedItem;
  const { mobileProductListingImage } = images;

  const handleRedirect = () => {
    gtmPromotionCmsClick(
      'MOBILE',
      `curated_${header}`,
      `curated_${widgetName}`,
      `${rowNumber}|${idx}`,
      getStoreName(),
      'home landing screen',
      'home screen'
    );
    window.location.href = `/d2c-fashion/c/83?q=:relevance&isCuratedWidget=true&widgetItemFilters=${filters}&widgetLevel=${widgetLevelFromBanners}&feedLogic=${feedLogic}&heading=${header}&${widgetLevelFromBanners?.toLowerCase() === 'user' ? 'showDefaultIfNoData='+showDefault : ''}`;
  };

  return (
    <div
      className={cx('curated_product')}
      id={`${rowNumber}|${idx}`}
      key={idx}
      onClick={handleRedirect}
    >
      <FallbackableImage
        imageCls={cx('curated_product-img', luxe ? 'luxe' : '')}
        src={mobileProductListingImage}
        alt={subheader}
      />
      <div className={cx('curated_product-details', luxe ? 'luxe' : '')}>
        {header && (
          <Text
            variant="T2"
            color={luxe ? 'Light_Black' : isAjiogram ? 'White' : 'Black'}
            weight={luxe ? 'regular' : 'bold'}
            textTransform={luxe ? 'is-uppercase' : 'none'}
            customClass={cx('curated_brand-name')}
          >
            {header}
          </Text>
        )}
        {subheader && (
          <Text
            variant={luxe ? 'T1' : 'T2'}
            color={luxe ? 'Light_Black' : isAjiogram ? 'Grey_White' : 'Dark_Grey'}
            weight="regular"
            textTransform="none"
            customClass={cx('curated_product-name')}
          >
            {subheader}
          </Text>
        )}
      </div>
    </div>
  );
};

const CuratedWidget = (props: CuratedWidgetProps) => {
  const { curatedWidgetData, luxe, rowNumber, banners, heading = '', isAjiogram = false} = props;
  const { widgetName = '', widgetLevel, curatedWidgetItemList = [] } = curatedWidgetData;
  const { feedLogic='', widgetLevel: widgetLevelFromBanners='', showDefault = true } = banners;

  let storeType = getStoreName() || 'ajio';
  const curatedWidgetBundleData = `curatedWidgetBundleData${storeType}`;
  const curatedWidgetImpressionsData = `curatedWidgetImpressionsData${storeType}`;

  const handleWindowUnload = () => {
    let { promotionArray = [], promotionData = {} } =
      getBundleCategoryImpressionArray(curatedWidgetBundleData, true) || {};
    if (promotionArray?.length > 0) {
      const { storeType, pageName, pageType } = promotionData;
      gtmPromotionCmsImpressions(promotionArray, storeType, '', pageType);
      localStorage.removeItem(curatedWidgetBundleData);
    }
  };

  const handleGaEventsforCuratedWidgets = (category, storeType, pageName, pageType) => {
    let promotionArray = getBundleCategoryImpressionArray(curatedWidgetBundleData); // Get the bundled data
    let isPromoIdExists = checkCategoryImpressionPromoId(
      category?.position,
      curatedWidgetImpressionsData
    ); // check if data has already made any impressions or not
    if (!isPromoIdExists) {
      promotionArray.push(category);
      setBundleCategoryImpressionArray(curatedWidgetBundleData, promotionArray, {
        storeType,
        pageName,
        pageType
      }); // Add data to the bundle
    }
    if (promotionArray.length === 5) {
      // Fire the impression when bundle lenght reaches 5 and empty the bundle array
      gtmPromotionCmsImpressions(promotionArray, storeType, '', pageType);
      emptyBundleCategoryImpressionArray(curatedWidgetBundleData);
    }
    // Add data to impressions array
    setCategoryImpressionPromoId(category?.position, curatedWidgetImpressionsData);
  };

  useEffect(() => {
    curatedWidgetItemList?.forEach((item, index) => {
      const { header } = item;
      let productId = document.getElementById(`${rowNumber}|${index}`);

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            handleGaEventsforCuratedWidgets(
              {
                id: 'MOBILE',
                name: `curated_${header}`,
                creative: `curated_${widgetName}`,
                position: `${rowNumber}|${index}`
              },
              storeType,
              'home landing screen',
              'home screen'
            );
          }
        },
        {
          threshold: 0.5
        }
      );
      if (productId) {
        observer.observe(productId);
      }
    });
    window.addEventListener('beforeunload', () => {
      handleWindowUnload();
    });

    return () => window.removeEventListener('beforeunload', handleWindowUnload);
  }, []);

  return (
    <div>
      <HorizontalScrollable
        items={curatedWidgetItemList}
        renderItem={(curatedItem: CuratedItem, idx: number) =>
          RenderCuratedProductCard({ curatedItem, idx, luxe, rowNumber, widgetName, feedLogic, widgetLevelFromBanners, showDefault, heading, isAjiogram })
        }
        hideScroll
        luxe={luxe}
        isDynamicProductList={false}
        productWidgetCard={false}
        containerClassNames={!luxe ? cx('recently-container') : ''}
        similarProductsVariant={1}
      />
    </div>
  );
};

export default CuratedWidget;
